import React, { useEffect, useMemo, useState } from 'react';
import { logger } from '../../utils/logger';
import { GiftIcon, PlusIcon, SearchIcon, LockIcon, CalendarIcon, ClockIcon, UserIcon, BriefcaseIcon, TagIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, PrinterIcon, BanIcon, SparklesIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Select } from '../ui/Select';
import { useData } from '../../contexts/DataContext';
import { useToast } from '../../contexts/ToastContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Patient, GiftCertificate } from '../../types';
import { formatCurrency, formatDate, getToday } from '../../utils/helpers';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
import { useConfirm } from '../../contexts/ConfirmContext';
interface CertRow {
  patient: Patient;
  cert: GiftCertificate;
}
// Cuántos días o menos para vencer se considera "por vencer" (alerta)
const EXPIRING_SOON_DAYS = 30;
// Estado efectivo: si está activo pero ya pasó su fecha, se muestra como "vencido"
function effectiveStatus(cert: GiftCertificate, today: string): GiftCertificate['status'] {
  if (cert.status === 'activo' && cert.expiresAt < today) return 'vencido';
  return cert.status;
}
function daysUntil(dateStr: string, today: string): number {
  const [y1, m1, d1] = dateStr.split('-').map(Number);
  const [y2, m2, d2] = today.split('-').map(Number);
  const a = new Date(y1, m1 - 1, d1).getTime();
  const b = new Date(y2, m2 - 1, d2).getTime();
  return Math.round((a - b) / 86400000);
}
// Resumen de servicios de un certificado (soporta uno o varios servicios).
function certServiceSummary(cert: GiftCertificate): string {
  if (cert.items && cert.items.length > 0) {
    return cert.items.map(it => it.serviceName).join(', ');
  }
  return cert.serviceName;
}
// Cuántos servicios incluye el certificado
function certServiceCount(cert: GiftCertificate): number {
  return cert.items && cert.items.length > 0 ? cert.items.length : 1;
}
// Suma 6 meses a una fecha YYYY-MM-DD (solo para previsualizar el vencimiento)
function addSixMonthsPreview(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const base = new Date(y, m - 1, d);
  base.setMonth(base.getMonth() + 6);
  const yy = base.getFullYear();
  const mm = String(base.getMonth() + 1).padStart(2, '0');
  const dd = String(base.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}
export function GiftCertificates() {
  const {
    patients,
    services,
    getService,
    getActiveOfferForService,
    getAppointment,
    addGiftCertificate,
    cancelGiftCertificate
  } = useData();
  const {
    canCreate,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('giftCertificates');
  const {
    showToast
  } = useToast();
  const {
    confirm
  } = useConfirm();
  const today = getToday();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 250);
    return () => clearTimeout(t);
  }, [searchTerm]);
  const [statusFilter, setStatusFilter] = useState<'todos' | 'activo' | 'canjeado' | 'vencido' | 'cancelado' | 'por_vencer'>('todos');
  // Create form
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    patientId: '',
    serviceIds: [] as string[],
    buyerName: '',
    notes: '',
    purchaseDate: today
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Detail / print
  const [detailRow, setDetailRow] = useState<CertRow | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  // Cancel confirm
  const [rowToCancel, setRowToCancel] = useState<CertRow | null>(null);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  // Flatten all certificates into rows
  const allRows: CertRow[] = useMemo(() => {
    const rows: CertRow[] = [];
    patients.forEach(patient => {
      ;
      (patient.giftCertificates || []).forEach(cert => {
        rows.push({
          patient,
          cert
        });
      });
    });
    return rows.sort((a, b) => new Date(b.cert.createdAt).getTime() - new Date(a.cert.createdAt).getTime());
  }, [patients]);
  const filteredRows = useMemo(() => {
    const term = debouncedSearch.toLowerCase();
    return allRows.filter(({
      patient,
      cert
    }) => {
      const matchesSearch = !term || patient.name.toLowerCase().includes(term) || certServiceSummary(cert).toLowerCase().includes(term) || cert.code.toLowerCase().includes(term) || (cert.buyerName || '').toLowerCase().includes(term);
      const eff = effectiveStatus(cert, today);
      let matchesStatus = true;
      if (statusFilter === 'por_vencer') {
        matchesStatus = eff === 'activo' && daysUntil(cert.expiresAt, today) <= EXPIRING_SOON_DAYS;
      } else if (statusFilter !== 'todos') {
        matchesStatus = eff === statusFilter;
      }
      return matchesSearch && matchesStatus;
    });
  }, [allRows, debouncedSearch, statusFilter, today]);
  const {
    paginatedItems: paginatedRows,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(filteredRows);
  // KPIs
  const kpis = useMemo(() => {
    let active = 0;
    let redeemed = 0;
    let expiringSoon = 0;
    let activeValue = 0;
    allRows.forEach(({
      cert
    }) => {
      const eff = effectiveStatus(cert, today);
      if (eff === 'activo') {
        active += 1;
        activeValue += cert.frozenPrice;
        if (daysUntil(cert.expiresAt, today) <= EXPIRING_SOON_DAYS) expiringSoon += 1;
      } else if (eff === 'canjeado') {
        redeemed += 1;
      }
    });
    return {
      active,
      redeemed,
      expiringSoon,
      activeValue
    };
  }, [allRows, today]);
  const patientOptions = useMemo(() => patients.map(p => ({
    value: p.id,
    label: p.name
  })), [patients]);
  const serviceOptions = useMemo(() => services.map(s => ({
    value: s.id,
    label: s.name
  })), [services]);
  // Live preview of the price for the create form. Cada servicio se evalúa
  // contra la FECHA DE EMISIÓN seleccionada (no contra hoy): si en esa fecha el
  // servicio no estaba en oferta, se emite a precio regular.
  const previewItems = useMemo(() => {
    if (!formData.purchaseDate) return [];
    return formData.serviceIds.map(sId => {
      const service = getService(sId);
      const offer = getActiveOfferForService(sId, formData.purchaseDate);
      const regularPrice = service?.price || 0;
      const frozenPrice = offer ? Math.max(0, regularPrice - regularPrice * (offer.discountPercent / 100)) : regularPrice;
      return {
        serviceId: sId,
        serviceName: service?.name || 'Servicio',
        regularPrice,
        frozenPrice,
        offerDiscountPercent: offer?.discountPercent
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.serviceIds, formData.purchaseDate, services]);
  const totalRegular = previewItems.reduce((s, it) => s + it.regularPrice, 0);
  const totalFrozen = previewItems.reduce((s, it) => s + it.frozenPrice, 0);
  const hasAnyOffer = previewItems.some(it => it.offerDiscountPercent);
  const openNewForm = () => {
    if (!canCreate('giftCertificates')) return;
    setFormData({
      patientId: '',
      serviceIds: [],
      buyerName: '',
      notes: '',
      purchaseDate: today
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.patientId) errors.patientId = 'Selecciona un paciente';
    if (formData.serviceIds.length === 0) errors.serviceIds = 'Selecciona al menos un servicio';
    if (!formData.purchaseDate) errors.purchaseDate = 'Selecciona la fecha de emisión';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    confirm({
      title: 'Crear Certificado',
      message: '¿Estás seguro de que deseas registrar este nuevo certificado de regalo?',
      confirmText: 'Guardar',
      onConfirm: async () => {
        try {
          await addGiftCertificate(formData.patientId, {
            items: previewItems.map(it => ({
              serviceId: it.serviceId,
              serviceName: it.serviceName,
              regularPrice: it.regularPrice,
              frozenPrice: it.frozenPrice,
              offerDiscountPercent: it.offerDiscountPercent
            })),
            buyerName: formData.buyerName,
            notes: formData.notes,
            purchaseDate: formData.purchaseDate
          });
          showToast({
            type: 'success',
            title: 'Certificado creado',
            message: `Se creó el certificado de regalo por ${formatCurrency(totalFrozen)}.`
          });
          setIsFormOpen(false);
        } catch (err) {
          logger.error('Error creando certificado de regalo:', err);
          showToast({
            type: 'error',
            title: 'Error',
            message: 'No se pudo crear el certificado. Revisa tu conexión.'
          });
        }
      }
    });
  };
  const openDetail = (row: CertRow) => {
    setDetailRow(row);
    setIsDetailOpen(true);
  };
  // Impresión elegante: abre una ventana independiente con un diseño de
  // certificado bonito (evita imprimir el modal completo). El <style> y las
  // etiquetas se arman por concatenación para que el transformador de build no
  // confunda el HTML dentro del template literal con JSX.
  const printCertificate = (cert: GiftCertificate, patient: Patient) => {
    const items = cert.items && cert.items.length > 0 ? cert.items : [{
      serviceId: cert.serviceId,
      serviceName: cert.serviceName,
      regularPrice: cert.regularPrice,
      frozenPrice: cert.frozenPrice,
      offerDiscountPercent: cert.offerDiscountPercent,
      redeemed: cert.status === 'canjeado'
    }];
    const esc = (s: string) => String(s).replace(/[&<>"']/g, c => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    } as Record<string, string>)[c]);
    const rows = items.map(it => `
        <tr>
          <td class="svc">${esc(it.serviceName)}${it.offerDiscountPercent ? `<span class="off">Oferta -${it.offerDiscountPercent}%</span>` : ''}</td>
          <td class="amt">${esc(formatCurrency(it.frozenPrice))}</td>
        </tr>`).join('');
    const hasOffer = cert.regularPrice > cert.frozenPrice;
    // Ícono de regalo (SVG en línea, en lugar del emoji 🎁) — un moño elegante
    // sobre una caja de regalo, en trazo blanco para lucir bien impreso.
    const giftSvg = `<svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" width="40" height="40">
      <path d="M12 8C10.8 5 9 4 7.6 4.7 6 5.6 7 8 12 8Z" fill="rgba(255,255,255,0.18)"/>
      <path d="M12 8C13.2 5 15 4 16.4 4.7 18 5.6 17 8 12 8Z" fill="rgba(255,255,255,0.18)"/>
      <circle cx="12" cy="8" r="1.3" fill="#ffffff" stroke="none"/>
      <rect x="3.5" y="8" width="17" height="4.2" rx="1"/>
      <path d="M5 12v6.8a1.2 1.2 0 0 0 1.2 1.2h11.6a1.2 1.2 0 0 0 1.2-1.2V12"/>
      <line x1="12" y1="8" x2="12" y2="20"/>
    </svg>`;
    const css = ['@page { size: A4 portrait; margin: 0; }', '* { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }', "body { font-family: 'Georgia', 'Times New Roman', serif; background: #f3f0fa; color: #1f2937; display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 32px; }", '.cert { position: relative; width: 640px; background: #ffffff; border-radius: 18px; overflow: hidden; box-shadow: 0 20px 60px rgba(109,40,217,0.18); border: 1px solid #e9e4f5; }', '.frame { position: absolute; inset: 14px; border: 2px solid rgba(168,85,247,0.35); border-radius: 12px; pointer-events: none; z-index: 3; }', ".frame::before { content: ''; position: absolute; inset: 5px; border: 1px solid rgba(168,85,247,0.2); border-radius: 9px; }", '.top { background: linear-gradient(135deg,#134e4a,#0c4a6e 55%,#1e3a8a); color: #fff; padding: 44px 40px 38px; text-align: center; position: relative; }', ".top::after { content:''; position:absolute; inset:0; background-image: repeating-linear-gradient(45deg, transparent, transparent 12px, rgba(255,255,255,.06) 12px, rgba(255,255,255,.06) 13px); }", ".brand { position: relative; font-family: 'Georgia', serif; letter-spacing: 4px; text-transform: uppercase; font-size: 12px; opacity: .85; }", '.medal { position: relative; width: 74px; height: 74px; margin: 10px auto 8px; border-radius: 50%; background: rgba(255,255,255,0.14); border: 2px solid rgba(255,255,255,0.55); display: flex; align-items: center; justify-content: center; box-shadow: 0 6px 18px rgba(0,0,0,0.18); }', '.title { position: relative; font-size: 30px; font-weight: 700; letter-spacing: 1px; }', '.subtitle { position: relative; font-size: 12px; opacity: .8; margin-top: 6px; letter-spacing: 2px; text-transform: uppercase; }', '.body { padding: 34px 44px 20px; text-align: center; position: relative; }', '.for { font-size: 12px; color: #9ca3af; text-transform: uppercase; letter-spacing: 2px; }', ".name { font-size: 27px; font-weight: 700; color: #4c1d95; margin: 6px 0 6px; font-family: 'Georgia', serif; }", '.rule { width: 120px; height: 0; margin: 0 auto 20px; border: none; border-top: 1px solid #ddd6fe; position: relative; }', ".rule::before { content: '\\2726'; position: absolute; top: -10px; left: 50%; transform: translateX(-50%); background: #fff; padding: 0 8px; color: #a855f7; font-size: 12px; }", '.seal { position: absolute; right: 26px; top: 8px; width: 88px; height: 88px; border-radius: 50%; border: 2px dashed #c4b5fd; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #7c3aed; transform: rotate(-12deg); opacity: .9; }', '.seal .s1 { font-size: 9px; letter-spacing: 1px; text-transform: uppercase; }', ".seal .s2 { font-family: 'Georgia', serif; font-size: 15px; font-weight: 700; margin: 1px 0; }", '.seal .s3 { font-size: 8px; letter-spacing: 1px; text-transform: uppercase; }', 'table { width: 100%; border-collapse: collapse; margin: 0 auto 18px; }', 'td { padding: 11px 6px; border-bottom: 1px dashed #e5e7eb; font-size: 15px; }', "td.svc { text-align: left; color: #374151; font-family: 'Helvetica', Arial, sans-serif; }", "td.amt { text-align: right; font-weight: 700; color: #7c3aed; white-space: nowrap; font-family: 'Helvetica', Arial, sans-serif; }", '.off { display:inline-block; margin-left:8px; background:#ecfdf5; color:#047857; border:1px solid #a7f3d0; border-radius:20px; font-size:10px; padding:1px 8px; font-weight:700; letter-spacing:.3px; }', ".total { display:flex; justify-content:space-between; align-items:baseline; margin-top: 6px; padding-top: 12px; border-top: 2px solid #ede9fe; font-family:'Helvetica',Arial,sans-serif; }", '.total .lbl { font-size: 13px; text-transform: uppercase; letter-spacing: 1px; color:#6b7280; }', '.total .val { font-size: 26px; font-weight: 800; color:#134e4a; }', '.total .reg { font-size: 13px; color:#9ca3af; text-decoration: line-through; margin-left: 10px; }', '.code-box { margin: 22px auto 6px; }', '.code-lbl { font-size: 10px; color:#9ca3af; text-transform: uppercase; letter-spacing: 2px; }', ".code { font-family: 'Courier New', monospace; font-size: 30px; font-weight: 700; letter-spacing: 6px; color:#111827; background: #f5f3ff; border: 2px dashed #c4b5fd; border-radius: 10px; padding: 10px 18px; display: inline-block; margin-top: 6px; }", ".meta { display:flex; justify-content:center; gap: 40px; margin: 22px 0 6px; font-family:'Helvetica',Arial,sans-serif; }", '.meta div { text-align:center; }', '.meta .k { font-size: 10px; color:#9ca3af; text-transform:uppercase; letter-spacing:1px; }', '.meta .v { font-size: 14px; font-weight: 700; color:#374151; margin-top: 3px; }', ".note { font-family:'Helvetica',Arial,sans-serif; font-size: 12px; color:#6b7280; font-style: italic; margin-top: 6px; }", ".foot { background:#faf7ff; border-top:1px solid #ede9fe; padding: 16px 44px; text-align:center; font-family:'Helvetica',Arial,sans-serif; font-size: 11px; color:#9ca3af; }", '.foot strong { color:#7c3aed; }', '@media print { body { background:#fff; padding:0; } .cert { box-shadow:none; border:none; } }'].join('\n');
    const styleOpen = '<' + 'style>';
    const styleClose = '<' + '/style>';
    const win = window.open('', '_blank', 'width=900,height=1100');
    if (!win) {
      showToast({
        type: 'error',
        title: 'No se pudo abrir la impresión',
        message: 'Permite las ventanas emergentes para imprimir el certificado.'
      });
      return;
    }
    win.document.write(`<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Certificado ${esc(cert.code)}</title>
${styleOpen}
${css}
${styleClose}
</head>
<body>
  <div class="cert">
    <div class="frame"></div>
    <div class="top">
      <div class="brand">Savidna</div>
      <div class="medal">${giftSvg}</div>
      <div class="title">Certificado de Regalo</div>
      <div class="subtitle">Gift Certificate</div>
    </div>
    <div class="body">
      <div class="seal">
        <span class="s1">Válido</span>
        <span class="s2">6 meses</span>
        <span class="s3">Savidna</span>
      </div>
      <div class="for">Otorgado a</div>
      <div class="name">${esc(patient.name)}</div>
      <hr class="rule" />
      <table>
        <tbody>${rows}</tbody>
      </table>
      <div class="total">
        <span class="lbl">Valor total</span>
        <span><span class="val">${esc(formatCurrency(cert.frozenPrice))}</span>${hasOffer ? `<span class="reg">${esc(formatCurrency(cert.regularPrice))}</span>` : ''}</span>
      </div>
      <div class="code-box">
        <div class="code-lbl">Código del certificado</div>
        <div class="code">${esc(cert.code)}</div>
      </div>
      <div class="meta">
        <div><div class="k">Emitido</div><div class="v">${esc(formatDate(cert.purchaseDate))}</div></div>
        <div><div class="k">Válido hasta</div><div class="v">${esc(formatDate(cert.expiresAt))}</div></div>
      </div>
      ${cert.buyerName ? `<div class="note">Un regalo de ${esc(cert.buyerName)}</div>` : ''}
      ${cert.notes ? `<div class="note">${esc(cert.notes)}</div>` : ''}
    </div>
    <div class="foot">
      Presenta este certificado con el código <strong>${esc(cert.code)}</strong> al momento de tu cita. Válido por 6 meses desde su emisión.
    </div>
  </div>
  <script>window.onload = function(){ setTimeout(function(){ window.focus(); window.print(); }, 300); };</script>
</body>
</html>`);
    win.document.close();
  };
  const confirmCancel = (row: CertRow) => {
    if (!canDelete('giftCertificates')) return;
    setRowToCancel(row);
    setIsCancelOpen(true);
  };
  const handleCancel = async () => {
    if (!rowToCancel) return;
    try {
      await cancelGiftCertificate(rowToCancel.patient.id, rowToCancel.cert.id);
      showToast({
        type: 'info',
        title: 'Certificado cancelado',
        message: `El certificado ${rowToCancel.cert.code} fue anulado.`
      });
    } catch (err) {
      logger.error('Error cancelando certificado:', err);
    } finally {
      setIsCancelOpen(false);
      setRowToCancel(null);
      setIsDetailOpen(false);
    }
  };
  const statusMeta = (eff: GiftCertificate['status']) => {
    switch (eff) {
      case 'activo':
        return {
          label: 'Activo',
          variant: 'success' as const
        };
      case 'canjeado':
        return {
          label: 'Canjeado',
          variant: 'info' as const
        };
      case 'vencido':
        return {
          label: 'Vencido',
          variant: 'danger' as const
        };
      case 'cancelado':
        return {
          label: 'Cancelado',
          variant: 'default' as const
        };
      default:
        return {
          label: eff,
          variant: 'default' as const
        };
    }
  };
  const statusTabs = [{
    key: 'todos',
    label: 'Todos'
  }, {
    key: 'activo',
    label: 'Activos'
  }, {
    key: 'por_vencer',
    label: 'Por vencer'
  }, {
    key: 'canjeado',
    label: 'Canjeados'
  }, {
    key: 'vencido',
    label: 'Vencidos'
  }, {
    key: 'cancelado',
    label: 'Cancelados'
  }];
  return <div className="space-y-6" data-id="element-3366" data-mp="vv6yxw1x7jf3">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl shadow-xl text-white" style={{
      background: 'linear-gradient(135deg,#134e4a,#0c4a6e,#1e3a8a)'
    }} data-id="element-3367" data-mp="10rnn2q94dxs6">
        
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 13px, rgba(255,255,255,1) 13px, rgba(255,255,255,1) 14px)'
      }} data-id="element-3368" data-mp="1sjv2jncw344z" />
        
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-3369" data-mp="27gceogswkmsl" />
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-3370" data-mp="2c8nme5tvxcvp" />
        <div className="relative z-10 p-6 pl-7" data-id="element-3371" data-mp="2m3uize2jzfeh">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5" data-id="element-3372" data-mp="2m18exoevfi10">
            <div data-id="element-3373" data-mp="2h2nebnvpiwc1">
              <div className="flex items-center gap-3 mb-2" data-id="element-3374" data-mp="2c5513lanpuy8">
                <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-3375" data-mp="2c3k8w2bteo3j">
                  <GiftIcon className="w-6 h-6 text-white" data-id="element-3376" data-mp="278emz3iex9fg" />
                </div>
                <h1 className="text-2xl font-bold text-white" data-id="element-3377" data-mp="2369w2cjcmqwi">
                  Certificados de Regalo
                </h1>
              </div>
              <p className="max-w-xl text-sm font-medium text-white/80" data-id="element-3378" data-mp="1tex6jrl0wctn">
                Compra anticipada de un servicio para regalar. El precio queda
                congelado (aunque la oferta termine) y es válido por 6 meses.
                {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-3379" data-mp="1jg7sk9w8zcio">
                    Solo lectura
                  </span>}
              </p>
            </div>
            <div className="flex gap-3" data-id="element-3380" data-mp="3r2801chlwzfl">
              {canCreate('giftCertificates') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3382" data-mp="3v5xmnweq16xz" />} onClick={openNewForm} className="bg-white text-teal-700 hover:bg-teal-50 shadow-lg shadow-teal-900/20" data-id="element-3381" data-mp="3v2i2wga3r9q8">
                
                  Nuevo Certificado
                </Button>}
            </div>
          </div>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-3383" data-mp="3cctp3zydu9y3">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/15" data-id="element-3384" data-mp="3cb8xai3gd53x">
              <p className="text-xs text-white/70 font-medium" data-id="element-3385" data-mp="3hava4qfdcjc7">Activos</p>
              <p className="text-xl font-bold text-white" data-id="element-3386" data-mp="3m4pn7b0gy02b">{kpis.active}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/15" data-id="element-3387" data-mp="2onhfx03221vs">
              <p className="text-xs text-white/70 font-medium" data-id="element-3388" data-mp="2tlj1psrv8fay">Canjeados</p>
              <p className="text-xl font-bold text-white" data-id="element-3389" data-mp="z7x2nej87bzx">{kpis.redeemed}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/15" data-id="element-3390" data-mp="v635hg88oq4i">
              <p className="text-xs text-white/70 font-medium flex items-center gap-1" data-id="element-3391" data-mp="18wydyrett57k">
                <AlertTriangleIcon className="w-3 h-3" data-id="element-3392" data-mp="145ge66ojhao6" /> Por vencer
              </p>
              <p className="text-xl font-bold text-white" data-id="element-3393" data-mp="1iumb1nzwmosw">
                {kpis.expiringSoon}
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/15" data-id="element-3394" data-mp="1duq87s2y13ex">
              <p className="text-xs text-white/70 font-medium" data-id="element-3395" data-mp="1ruhro8c8bc0b">Valor activo</p>
              <p className="text-xl font-bold text-white" data-id="element-3396" data-mp="1mvy5q7xmy07y">
                {formatCurrency(kpis.activeValue)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-3397" data-mp="3ooci6rheucgz">
          <LockIcon className="w-4 h-4" data-id="element-3398" data-mp="294loh99pwgce" />
          <span data-id="element-3399" data-mp="293k4gfaq4jij">
            Solo tienes permisos de lectura. No puedes crear ni cancelar
            certificados.
          </span>
        </div>}

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center" data-id="element-3400" data-mp="1ze0iv1vmmbjz">
        <div className="w-full md:w-96 relative" data-id="element-3401" data-mp="1lfkevip5ez5j">
          <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-3402" data-mp="1ldzgtj3mky2x" />
          <Input style={{
          paddingLeft: '2.5rem'
        }} placeholder="Buscar por paciente, servicio o código..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-3403" data-mp="1qc0zw411wxee" />
          
        </div>
        <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-3404" data-mp="3asxlxfonb1ac">
          {statusTabs.map(tab => <button key={tab.key} onClick={() => setStatusFilter(tab.key as any)} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${statusFilter === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-3405" data-mp="imyeo3oj1v2f">
            
              {tab.label}
            </button>)}
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3406" data-mp="xixiv38inb2a">
        {paginatedRows.length === 0 ? <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400" data-id="element-3407" data-mp="skcqfpzi9voe">
            <div className="flex flex-col items-center justify-center" data-id="element-3408" data-mp="1q11nz7hfabrz">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-3409" data-mp="1l99wmuqg020s">
                <GiftIcon className="w-8 h-8 text-gray-400" data-id="element-3410" data-mp="1slznwr3jq1zc" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-3411" data-mp="1xizo4ldpsaij">
                No se encontraron certificados
              </h3>
              <p className="text-sm" data-id="element-3412" data-mp="22l8hvjyevglp">
                {searchTerm || statusFilter !== 'todos' ? 'Intenta con otros filtros de búsqueda' : 'Crea un certificado de regalo para comenzar'}
              </p>
            </div>
          </div> : paginatedRows.map(({
        patient,
        cert
      }) => {
        const eff = effectiveStatus(cert, today);
        const meta = statusMeta(eff);
        const days = daysUntil(cert.expiresAt, today);
        const expiringSoon = eff === 'activo' && days <= EXPIRING_SOON_DAYS && days >= 0;
        return <div key={cert.id} onClick={() => openDetail({
          patient,
          cert
        })} className="group relative flex flex-col rounded-2xl ring-1 ring-gray-200/80 dark:ring-gray-700/60 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 overflow-hidden cursor-pointer bg-white dark:bg-gray-800" data-id="element-3413" data-mp="2u7yutvynh48m">
              
                {/* Ticket top strip */}
                <div className="relative px-5 pt-4 pb-3 bg-gradient-to-br from-teal-800 to-blue-900 text-white" data-id="element-3414" data-mp="3pjxytxybafl1">
                  <div className="absolute inset-0 opacity-[0.12] pointer-events-none" style={{
              backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(255,255,255,1) 8px, rgba(255,255,255,1) 9px)'
            }} data-id="element-3415" data-mp="31rhhjpmeerms" />
                
                  <div className="relative flex items-start justify-between gap-2" data-id="element-3416" data-mp="2j2atro6qz9u4">
                    <div className="min-w-0" data-id="element-3417" data-mp="2e4994ytob2gj">
                      <p className="text-[10px] uppercase tracking-widest text-white/70 font-semibold flex items-center gap-1" data-id="element-3418" data-mp="2e3hpmmdcov6m">
                        <GiftIcon className="w-3 h-3" data-id="element-3419" data-mp="tk5ixptnkq6b" /> Certificado de Regalo
                      </p>
                      <p className="font-mono text-lg font-bold tracking-wider" data-id="element-3420" data-mp="yei3622ye5j4">
                        {cert.code}
                      </p>
                    </div>
                    <Badge variant={meta.variant} data-id="element-3421" data-mp="onf2v2hulukw">{meta.label}</Badge>
                  </div>
                </div>
                {/* Perforation */}
                <div className="relative h-0" data-id="element-3422" data-mp="1hbb2og2ps2lw">
                  <div className="absolute -left-2 -top-2 w-4 h-4 rounded-full bg-gray-50 dark:bg-gray-900" data-id="element-3423" data-mp="1hb19ykqabhci" />
                  <div className="absolute -right-2 -top-2 w-4 h-4 rounded-full bg-gray-50 dark:bg-gray-900" data-id="element-3424" data-mp="13bi9me3ptaar" />
                </div>
                <div className="p-5 flex-1 space-y-2.5" data-id="element-3425" data-mp="3o291v28wpuoc">
                  <div className="flex items-center gap-1.5 text-sm text-gray-900 dark:text-white font-semibold" data-id="element-3426" data-mp="3o0pl7ajc9hm1">
                    <UserIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-3427" data-mp="3s1hw087uzukf" />
                    {patient.name}
                  </div>
                  <div className="flex items-start gap-1.5 text-sm text-gray-600 dark:text-gray-300" data-id="element-3428" data-mp="2w2opyhcey7jp">
                    <BriefcaseIcon className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-3429" data-mp="304s45dqgvq8o" />
                    <span className="min-w-0" data-id="element-3430" data-mp="353d216i8xxty">
                      {certServiceSummary(cert)}
                      {certServiceCount(cert) > 1 && <span className="ml-1 text-[10px] font-semibold text-teal-600 dark:text-teal-400" data-id="element-3431" data-mp="28exeffyp5yjh">
                          ({certServiceCount(cert)} servicios)
                        </span>}
                    </span>
                  </div>
                  <div className="flex items-end justify-between pt-1" data-id="element-3432" data-mp="2m50q5czya9uo">
                    <div data-id="element-3433" data-mp="1opmx5xjaz5j9">
                      {cert.regularPrice > cert.frozenPrice ? <>
                          <p className="text-[11px] text-gray-400 line-through leading-none" data-id="element-3434" data-mp="1tny4a0i7r1xs">
                            {formatCurrency(cert.regularPrice)}
                          </p>
                          <p className="text-xl font-extrabold text-teal-600 dark:text-teal-400 leading-tight" data-id="element-3435" data-mp="1h63y6htnz1p1">
                            {formatCurrency(cert.frozenPrice)}
                          </p>
                        </> : <p className="text-xl font-extrabold text-teal-600 dark:text-teal-400" data-id="element-3436" data-mp="1844gdnatrtl1">
                          {formatCurrency(cert.frozenPrice)}
                        </p>}
                    </div>
                    {cert.regularPrice > cert.frozenPrice ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-300 dark:border-emerald-700 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400" data-id="element-3437" data-mp="tfi27owxp7ve">
                        <TagIcon className="w-3 h-3" data-id="element-3438" data-mp="kho2s53vhy40" />
                        Oferta congelada
                      </span> : null}
                  </div>
                  <div className={`flex items-center gap-1.5 text-xs pt-1 ${expiringSoon ? 'text-amber-600 dark:text-amber-400 font-semibold' : 'text-gray-400 dark:text-gray-500'}`} data-id="element-3439" data-mp="5s364h5ll0a8">
                  
                    <ClockIcon className="w-3.5 h-3.5" data-id="element-3440" data-mp="3s278n5kjjrtn" />
                    {eff === 'canjeado' ? `Canjeado el ${cert.redeemedDate ? formatDate(cert.redeemedDate) : '—'}` : eff === 'vencido' ? `Venció el ${formatDate(cert.expiresAt)}` : `Vence el ${formatDate(cert.expiresAt)}${expiringSoon ? ` · ${days} día${days === 1 ? '' : 's'}` : ''}`}
                  </div>
                </div>
              </div>;
      })}
      </div>

      <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-3441" data-mp="jqb1ikmzjtja" />
      

      {/* Create Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title="Nuevo Certificado de Regalo" size="md" icon={<GiftIcon className="w-5 h-5" data-id="element-3443" data-mp="35qmdafs1jd45" />} accentColor="sky" data-id="element-3442" data-mp="2wx9dquzh1d93">
        
        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-3444" data-mp="31pkaxq2tukxy">
          <Select label="Paciente (titular del certificado)" options={patientOptions} value={formData.patientId} onChange={v => setFormData({
          ...formData,
          patientId: v
        })} error={formErrors.patientId} placeholder="Selecciona la persona" searchable data-id="element-3445" data-mp="31s6s8hfisetd" />
          
          {/* Servicios: se pueden agregar uno o varios */}
          <div data-id="element-3446" data-mp="29nqvz14ncmi">
            <Select label="Servicios (puedes agregar varios)" options={serviceOptions.filter(o => !formData.serviceIds.includes(o.value))} value="" onChange={v => {
            if (!v) return;
            setFormData(prev => ({
              ...prev,
              serviceIds: prev.serviceIds.includes(v) ? prev.serviceIds : [...prev.serviceIds, v]
            }));
            setFormErrors(prev => ({
              ...prev,
              serviceIds: ''
            }));
          }} error={formErrors.serviceIds} placeholder="Agrega un servicio al certificado" searchable data-id="element-3447" data-mp="29470crx2e5u" />
            
            {formData.serviceIds.length > 0 && <div className="mt-2 space-y-1.5" data-id="element-3448" data-mp="1qac86u0g8px2">
                {previewItems.map(it => <div key={it.serviceId} className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2" data-id="element-3449" data-mp="1v95lalbtqewp">
                
                    <div className="min-w-0 flex items-center gap-2" data-id="element-3450" data-mp="3jtb5dhbc8gbn">
                      <BriefcaseIcon className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-3451" data-mp="3fqeukcs1hoi9" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate" data-id="element-3452" data-mp="3arleqdpa5lex">
                        {it.serviceName}
                      </span>
                      {it.offerDiscountPercent ? <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-300 dark:border-emerald-700 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 flex-shrink-0" data-id="element-3453" data-mp="7becmbmijw89">
                          <TagIcon className="w-2.5 h-2.5" data-id="element-3454" data-mp="3ormh5xl710wi" />-
                          {it.offerDiscountPercent}%
                        </span> : null}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0" data-id="element-3455" data-mp="q3fm07wwz4vx">
                      <span className="text-sm font-semibold text-teal-600 dark:text-teal-400" data-id="element-3456" data-mp="l9l3v8wyixyy">
                        {formatCurrency(it.frozenPrice)}
                      </span>
                      <button type="button" onClick={() => setFormData(prev => ({
                  ...prev,
                  serviceIds: prev.serviceIds.filter(id => id !== it.serviceId)
                }))} className="p-1 text-gray-400 hover:text-rose-600 rounded transition-colors" title="Quitar servicio" data-id="element-3457" data-mp="2jj1txio29nxk">
                    
                        <XCircleIcon className="w-4 h-4" data-id="element-3458" data-mp="1mvx2p6vmnu46" />
                      </button>
                    </div>
                  </div>)}
              </div>}
          </div>

          {/* Emission date */}
          <div data-id="element-3459" data-mp="3m76oe5n9t9bu">
            <Input type="date" label="Fecha de emisión" icon={<CalendarIcon className="w-4 h-4" data-id="element-3461" data-mp="5hcazbyt039" />} max={today} value={formData.purchaseDate} onChange={e => setFormData({
            ...formData,
            purchaseDate: e.target.value
          })} error={formErrors.purchaseDate} data-id="element-3460" data-mp="3m7q3leljdjm6" />
            
            {formData.purchaseDate && <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5 flex items-center gap-1" data-id="element-3462" data-mp="iw8i5qwqu8cw">
                <ClockIcon className="w-3 h-3" data-id="element-3463" data-mp="dxe2gxu2ozx0" />
                Vence el{' '}
                <span className="font-medium" data-id="element-3464" data-mp="dzi4194i1mu4">
                  {formatDate(addSixMonthsPreview(formData.purchaseDate))}
                </span>{' '}
                (6 meses después)
              </p>}
          </div>

          {/* Total preview */}
          {previewItems.length > 0 && <div className="rounded-xl border border-teal-200 dark:border-teal-800 bg-teal-50/60 dark:bg-teal-900/20 p-4" data-id="element-3465" data-mp="2tq3agxq3rexj">
              <div className="flex items-center justify-between" data-id="element-3466" data-mp="3e7v63if33blp">
                <div data-id="element-3467" data-mp="3e5imz1xlav72">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400" data-id="element-3468" data-mp="3j6g4dcarzl7i">
                    Total a pagar (congelado)
                    {previewItems.length > 1 ? ` · ${previewItems.length} servicios` : ''}
                  </p>
                  <div className="flex items-baseline gap-2" data-id="element-3469" data-mp="3s0uihbjkkpp1">
                    <p className="text-2xl font-extrabold text-teal-700 dark:text-teal-300" data-id="element-3470" data-mp="2va290wr55vw8">
                      {formatCurrency(totalFrozen)}
                    </p>
                    {hasAnyOffer && totalRegular > totalFrozen && <span className="text-sm text-gray-400 line-through" data-id="element-3471" data-mp="34bs2070o92w4">
                        {formatCurrency(totalRegular)}
                      </span>}
                  </div>
                </div>
                {hasAnyOffer ? <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-xs font-semibold" data-id="element-3472" data-mp="oh9myqq4a1n2">
                    <SparklesIcon className="w-3.5 h-3.5" data-id="element-3473" data-mp="tmv8pm06i7rg" />
                    Con oferta
                  </span> : <span className="text-xs text-gray-400" data-id="element-3474" data-mp="ov3r40z03cdg">
                    Sin oferta activa
                  </span>}
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2 flex items-center gap-1" data-id="element-3475" data-mp="fvqdcw3ug1mx">
                <ClockIcon className="w-3 h-3" data-id="element-3476" data-mp="awwzv3ok4amr" />
                Válido por 6 meses. Estos precios se conservan aunque la oferta
                termine.
              </p>
            </div>}

          <Input label="Comprado por (opcional)" value={formData.buyerName} onChange={e => setFormData({
          ...formData,
          buyerName: e.target.value
        })} placeholder="Ej: María (madrina)" data-id="element-3477" data-mp="1w8o6fmmymd1" />
          
          <Textarea label="Notas (opcional)" value={formData.notes} onChange={e => setFormData({
          ...formData,
          notes: e.target.value
        })} rows={2} placeholder="Ej: Regalo de cumpleaños" data-id="element-3478" data-mp="2krwwppfllp5o" />
          
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100 dark:border-gray-700 modal-actions" data-id="element-3479" data-mp="25vejjynxsv3x">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-3480" data-mp="33hbsp5gffo37">
              
              Cancelar
            </Button>
            <Button type="submit" data-id="element-3481" data-mp="2tqh8s7qnl0kx">Crear Certificado</Button>
          </div>
        </form>
      </Modal>

      {/* Detail / Print Modal */}
      <Modal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} title="Detalle del Certificado" size="md" icon={<GiftIcon className="w-5 h-5" data-id="element-3483" data-mp="3hgus99v69r3w" />} accentColor="sky" data-id="element-3482" data-mp="2or4jlgcb7hf6">
        
        {detailRow && (() => {
        const {
          patient,
          cert
        } = detailRow;
        const eff = effectiveStatus(cert, today);
        const meta = statusMeta(eff);
        const redeemedApt = cert.redeemedAppointmentId ? getAppointment(cert.redeemedAppointmentId) : undefined;
        return <div className="space-y-4" data-id="element-3484" data-mp="7m8kwaxftppo">
                {/* Printable certificate */}
                <div id="gift-cert-print" className="relative rounded-2xl overflow-hidden ring-1 ring-teal-200 dark:ring-teal-800" data-id="element-3485" data-mp="ciyzsuxiaf9i">
                
                  <div className="bg-gradient-to-br from-teal-800 via-blue-800 to-blue-900 text-white p-6 text-center relative" data-id="element-3486" data-mp="ld3nx6l99clc">
                    <div className="absolute inset-0 opacity-[0.1] pointer-events-none" style={{
                backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,1) 10px, rgba(255,255,255,1) 11px)'
              }} data-id="element-3487" data-mp="qadmbbqmgylb" />
                  
                    <div className="relative" data-id="element-3488" data-mp="5mvxszwppz1r">
                      <GiftIcon className="w-10 h-10 mx-auto mb-2" data-id="element-3489" data-mp="5jzz39d1j6c9" />
                      <p className="text-xs uppercase tracking-[0.3em] text-white/80" data-id="element-3490" data-mp="kbsf7nknsof1">
                        Certificado de Regalo
                      </p>
                      <p className="font-mono text-3xl font-extrabold tracking-widest mt-1" data-id="element-3491" data-mp="tbf459w7c1r7">
                        {cert.code}
                      </p>
                      <p className="text-[10px] text-white/70 mt-1" data-id="element-3492" data-mp="oc2md3k6tkvh">
                        Código único · preséntalo al canjear
                      </p>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-900 p-5 space-y-3" data-id="element-3493" data-mp="ybjlf24xp0ih">
                    <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-3494" data-mp="3520xkca9mw28">
                      <div data-id="element-3495" data-mp="302oc9vz1lx1r">
                        <p className="text-xs text-gray-400" data-id="element-3496" data-mp="303g897wjxwsk">Titular</p>
                        <p className="font-semibold text-gray-900 dark:text-white" data-id="element-3497" data-mp="1fjklz1aoffhe">
                          {patient.name}
                        </p>
                      </div>
                      <div data-id="element-3498" data-mp="15uar5dog7ipl">
                        <p className="text-xs text-gray-400" data-id="element-3499" data-mp="15v2n4plyjige">Válido hasta</p>
                        <p className="font-semibold text-gray-900 dark:text-white" data-id="element-3500" data-mp="1at48jh6uvtv1">
                          {formatDate(cert.expiresAt)}
                        </p>
                      </div>
                      <div className="col-span-2" data-id="element-3501" data-mp="10uy5uxd86jp4">
                        <p className="text-xs text-gray-400" data-id="element-3502" data-mp="10wreujbb9g0a">Valor total</p>
                        <p className="font-extrabold text-teal-600 dark:text-teal-400" data-id="element-3503" data-mp="mwgmfbucrtd0">
                          {formatCurrency(cert.frozenPrice)}
                          {cert.regularPrice > cert.frozenPrice ? <span className="ml-2 text-[11px] font-medium text-gray-400 line-through" data-id="element-3504" data-mp="2har9pfvaufgo">
                              {formatCurrency(cert.regularPrice)}
                            </span> : null}
                        </p>
                      </div>
                    </div>
                    {/* Servicios incluidos */}
                    <div data-id="element-3505" data-mp="d5d0byjkyrdb">
                      <p className="text-xs text-gray-400 mb-1.5" data-id="element-3506" data-mp="d4k25lyota6s">
                        {certServiceCount(cert) > 1 ? 'Servicios incluidos' : 'Servicio'}
                      </p>
                      <ul className="space-y-1.5" data-id="element-3507" data-mp="38qf38ncdyar">
                        {(cert.items && cert.items.length > 0 ? cert.items : [{
                    serviceId: cert.serviceId,
                    serviceName: cert.serviceName,
                    regularPrice: cert.regularPrice,
                    frozenPrice: cert.frozenPrice,
                    offerDiscountPercent: cert.offerDiscountPercent,
                    redeemed: cert.status === 'canjeado'
                  }]).map((it, idx) => <li key={`${it.serviceId}-${idx}`} className="flex items-center justify-between gap-2 text-sm" data-id="element-3508" data-mp="1r7khdy1d0xit" data-mpi={idx}>
                        
                            <span className="flex items-center gap-1.5 min-w-0 text-gray-900 dark:text-white" data-id="element-3509" data-mp="2119mwqlvvbw3">
                              {it.redeemed ? <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" data-id="element-3510" data-mp="18froe3majcb3" /> : <BriefcaseIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" data-id="element-3511" data-mp="1deusnlug5ei0" />}
                              <span className={`truncate ${it.redeemed ? 'line-through text-gray-400 dark:text-gray-500' : 'font-medium'}`} data-id="element-3512" data-mp="1he3meqphlcdk">
                            
                                {it.serviceName}
                              </span>
                              {it.offerDiscountPercent ? <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 flex-shrink-0" data-id="element-3513" data-mp="1x0ge8k6w29pd">
                                  -{it.offerDiscountPercent}%
                                </span> : null}
                            </span>
                            <span className="text-sm font-semibold text-teal-600 dark:text-teal-400 flex-shrink-0" data-id="element-3514" data-mp="21rgdhw3hd3v1">
                              {formatCurrency(it.frozenPrice)}
                            </span>
                          </li>)}
                      </ul>
                    </div>
                    {cert.buyerName && <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-3515" data-mp="1n43jrk3irrtk">
                        Un regalo de{' '}
                        <span className="font-medium" data-id="element-3516" data-mp="1j31h0tae8xmu">{cert.buyerName}</span>
                      </p>}
                  </div>
                </div>

                {/* Status + meta */}
                <div className="flex items-center justify-between" data-id="element-3517" data-mp="2uigf60ec9vjj">
                  <Badge variant={meta.variant} data-id="element-3518" data-mp="31y1vevlbb9ue">{meta.label}</Badge>
                  <span className="text-xs text-gray-400" data-id="element-3519" data-mp="36yzo423y7ma5">
                    Emitido el {formatDate(cert.purchaseDate)}
                  </span>
                </div>

                {cert.notes && <p className="text-sm text-gray-500 dark:text-gray-400 italic" data-id="element-3520" data-mp="2wzqksin70vb4">
                    {cert.notes}
                  </p>}

                {/* Identification / redemption info */}
                <div className="rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700 p-4 text-sm space-y-1.5" data-id="element-3521" data-mp="3po5xuzmrl3jt">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide" data-id="element-3522" data-mp="3ay8fx1ffha32">
                    Cómo se identifica la cita
                  </p>
                  {eff === 'canjeado' ? <p className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5" data-id="element-3523" data-mp="caaz222tzwsc">
                      <CheckCircleIcon className="w-4 h-4" data-id="element-3524" data-mp="28d7b9j6civo0" />
                      Canjeado
                      {cert.redeemedDate ? ` el ${formatDate(cert.redeemedDate)}` : ''}
                      {redeemedApt ? ` — cita del ${formatDate(redeemedApt.date)}` : ''}
                    </p> : eff === 'activo' ? <p className="text-gray-600 dark:text-gray-300" data-id="element-3525" data-mp="2rcblgv9mivh9">
                      Al completar una cita de{' '}
                      <span className="font-medium" data-id="element-3526" data-mp="2nagi7l84u80u">{patient.name}</span> para{' '}
                      <span className="font-medium" data-id="element-3527" data-mp="2ibddy2zz85tx">
                        {certServiceSummary(cert)}
                      </span>
                      {certServiceCount(cert) > 1 ? ' (cada servicio se canjea al completar su cita)' : ''}
                      , el sistema ofrecerá canjear este certificado y marcará
                      esa cita como pagada con él.
                    </p> : <p className="text-gray-500 dark:text-gray-400" data-id="element-3528" data-mp="ruhtvrlpco2b">
                      Este certificado ya no puede canjearse.
                    </p>}
                </div>

                <div className="flex justify-end gap-3 pt-3 border-t border-gray-100 dark:border-gray-700 modal-actions print:hidden" data-id="element-3529" data-mp="11rcv4i9d12ka">
                  <Button variant="outline" onClick={() => printCertificate(cert, patient)} leftIcon={<PrinterIcon className="w-4 h-4" data-id="element-3531" data-mp="1firdcmvg0zqa" />} data-id="element-3530" data-mp="16jnspegmoc9o">
                  
                    Imprimir
                  </Button>
                  {!readOnly && canDelete('giftCertificates') && eff === 'activo' && <Button variant="danger" onClick={() => confirmCancel(detailRow)} leftIcon={<BanIcon className="w-4 h-4" data-id="element-3533" data-mp="3mbts4o8wdf8u" />} data-id="element-3532" data-mp="1pgoyh1i0ageo">
                  
                        Anular
                      </Button>}
                </div>
              </div>;
      })()}
      </Modal>

      {/* Cancel Confirmation */}
      <Modal isOpen={isCancelOpen} onClose={() => setIsCancelOpen(false)} title="Anular Certificado" size="sm" icon={<XCircleIcon className="w-5 h-5" data-id="element-3535" data-mp="1sg8zxuca4cww" />} accentColor="rose" data-id="element-3534" data-mp="26aigmue7nvfb">
        
        <div className="space-y-4" data-id="element-3536" data-mp="1xheu7wll888j">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-3537" data-mp="2l70tsl5vgph2">
            ¿Deseas anular el certificado{' '}
            <span className="font-mono font-semibold" data-id="element-3538" data-mp="2p8urckkrtdd0">
              {rowToCancel?.cert.code}
            </span>{' '}
            de "{rowToCancel?.patient.name}"? Quedará marcado como cancelado y
            no podrá canjearse. El registro se conserva en la bitácora.
          </p>
          <div className="flex justify-end gap-3 modal-actions" data-id="element-3539" data-mp="ztekjbubk3qa">
            <Button variant="outline" onClick={() => setIsCancelOpen(false)} data-id="element-3540" data-mp="zs3f77l86nx2">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleCancel} data-id="element-3541" data-mp="14q4zfvue0ta4">
              <BanIcon className="w-4 h-4 mr-1" data-id="element-3542" data-mp="x7nix5o3ge7n" /> Anular certificado
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}